import { useState } from "react";
import { toast } from "sonner";
import { Mail, Loader2, Check } from "lucide-react";
import { API } from "@/lib/brand";

/**
 * Small "get new posts by email" box for the bottom of every article.
 * POSTs to /api/newsletter/subscribe. Idempotent — safe to resubmit.
 */
export default function NewsletterBox({ source }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | submitting | done

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please add your email");
      return;
    }
    setState("submitting");
    try {
      const res = await fetch(`${API}/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), source: source || "journal-post" }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || "Could not subscribe");
      }
      const data = await res.json();
      setState("done");
      toast.success(
        data.already_subscribed ? "You're already on the list" : "You're on the list",
        { description: "New posts will land in your inbox — no spam." }
      );
    } catch (err) {
      setState("idle");
      toast.error(err.message || "Something went wrong");
    }
  };

  if (state === "done") {
    return (
      <div
        className="mt-12 card-paper p-6 md:p-8 flex items-start gap-3"
        data-testid="newsletter-done"
      >
        <Check size={20} className="text-brass-deep shrink-0 mt-0.5" />
        <div>
          <div className="font-display text-lg text-ink leading-snug">You're on the list</div>
          <p className="text-[13.5px] text-ink-soft leading-relaxed mt-1">
            New posts will land in your inbox — no spam, no more than one email a fortnight.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-12 card-paper p-6 md:p-8" data-testid="newsletter-box">
      <div className="flex items-start gap-3 mb-4">
        <Mail size={20} className="text-brass-deep shrink-0 mt-0.5" />
        <div>
          <div className="eyebrow mb-1">Get new posts by email</div>
          <h3 className="font-display text-xl text-ink leading-snug">
            One quiet note when a new Journal post goes live.
          </h3>
          <p className="text-[13.5px] text-ink-soft leading-relaxed mt-1.5">
            No sales pitch, no spam — just the writing, whenever we publish.
          </p>
        </div>
      </div>
      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2 sm:gap-3">
        <input
          type="email"
          className="fld flex-1"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="newsletter-email"
          required
        />
        <button
          type="submit"
          disabled={state === "submitting"}
          className="btn-brass disabled:opacity-60 justify-center"
          data-testid="newsletter-submit"
        >
          {state === "submitting" ? <Loader2 size={16} className="animate-spin" /> : null}
          Subscribe
        </button>
      </form>
      <p className="text-[11px] text-ink-soft/85 mt-3">
        Unsubscribe any time — every email includes a one-click opt-out link.
      </p>
    </div>
  );
}
